export const SOLUTION_CANVAS_DRAFT_VERSION = 1;
export const SOLUTION_CANVAS_CATALOG = Object.freeze({
    "channel.voice": Object.freeze({
        kind: "channel",
        eyebrow: "Channel",
        description: "Voice interaction",
        channelKey: "voice",
    }),
    "channel.whatsapp": Object.freeze({
        kind: "channel",
        eyebrow: "Channel",
        description: "Meta WhatsApp interaction",
        channelKey: "meta-whatsapp",
    }),
    "context.conversation": Object.freeze({
        kind: "conversation",
        eyebrow: "Context",
        description: "Conversation context and history",
        owner: "conversation",
        capabilityKey: "conversation.context",
    }),
    "ai.agent": Object.freeze({
        kind: "agent",
        eyebrow: "AI",
        description: "AI orchestration and decisions",
        owner: "agent",
        capabilityKey: "agent.orchestration",
    }),
    "ai.knowledge": Object.freeze({
        kind: "knowledge",
        eyebrow: "Grounding",
        description: "Approved knowledge retrieval",
        owner: "knowledge",
        capabilityKey: "knowledge.query",
    }),
    "business.customer": Object.freeze({
        kind: "business",
        eyebrow: "Business",
        description: "Customer identity and context",
        owner: "customer",
        capabilityKey: "customer.identity.read",
    }),
    "business.scheduling": Object.freeze({
        kind: "business",
        eyebrow: "Business",
        description: "Availability and appointments",
        owner: "scheduling",
        capabilityKey: "scheduling.appointment",
    }),
    "flow.automation": Object.freeze({
        kind: "automation",
        eyebrow: "Flow",
        description: "Post-event automation",
        owner: "automation",
        capabilityKey: "automation.appointment-followup",
    }),
    "control.human": Object.freeze({
        kind: "human",
        eyebrow: "Control",
        description: "Human handoff and validation",
        owner: "conversation",
        capabilityKey: "conversation.human-control",
    }),
});
const ID = /^[a-z0-9](?:[a-z0-9._:-]{0,95})$/;
function issue(issues, code, path, message) {
    issues.push(Object.freeze({ code, path, message }));
}
export function validateSolutionCanvasDraft(value) {
    const issues = [];
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return Object.freeze({
            valid: false,
            issues: Object.freeze([
                Object.freeze({ code: "INVALID_CANVAS", path: "$", message: "canvas must be an object" }),
            ]),
        });
    }
    const canvas = value;
    if (canvas.schemaVersion !== 1)
        issue(issues, "INVALID_SCHEMA_VERSION", "schemaVersion", "schemaVersion must be 1");
    if (typeof canvas.name !== "string" || canvas.name.trim().length === 0 || canvas.name.trim().length > 120) {
        issue(issues, "INVALID_NAME", "name", "name must contain 1 to 120 characters");
    }
    if (typeof canvas.description !== "string" || canvas.description.length > 500) {
        issue(issues, "INVALID_DESCRIPTION", "description", "description must contain at most 500 characters");
    }
    const nodes = Array.isArray(canvas.nodes) ? canvas.nodes : [];
    const edges = Array.isArray(canvas.edges) ? canvas.edges : [];
    if (nodes.length > 64)
        issue(issues, "TOO_MANY_NODES", "nodes", "canvas supports at most 64 nodes");
    if (edges.length > 128)
        issue(issues, "TOO_MANY_EDGES", "edges", "canvas supports at most 128 edges");
    const nodeIds = new Set();
    nodes.forEach((node, index) => {
        const path = `nodes[${index}]`;
        if (!node || typeof node !== "object") {
            issue(issues, "INVALID_NODE", path, "node must be an object");
            return;
        }
        if (typeof node.id !== "string" || !ID.test(node.id)) {
            issue(issues, "INVALID_NODE_ID", path + ".id", "node id must be canonical");
        }
        else {
            if (nodeIds.has(node.id))
                issue(issues, "DUPLICATE_NODE_ID", path + ".id", "node id must be unique");
            nodeIds.add(node.id);
        }
        if (!(typeof node.type === "string" && node.type in SOLUTION_CANVAS_CATALOG)) {
            issue(issues, "INVALID_NODE_TYPE", path + ".type", "node type is not supported");
        }
        if (typeof node.label !== "string" || node.label.trim().length === 0 || node.label.trim().length > 120) {
            issue(issues, "INVALID_NODE_LABEL", path + ".label", "node label must contain 1 to 120 characters");
        }
        if (!node.position ||
            typeof node.position.x !== "number" ||
            !Number.isFinite(node.position.x) ||
            typeof node.position.y !== "number" ||
            !Number.isFinite(node.position.y)) {
            issue(issues, "INVALID_NODE_POSITION", path + ".position", "node position must use finite world coordinates");
        }
    });
    const edgeIds = new Set();
    const edgePairs = new Set();
    edges.forEach((edge, index) => {
        const path = `edges[${index}]`;
        if (!edge || typeof edge !== "object") {
            issue(issues, "INVALID_EDGE", path, "edge must be an object");
            return;
        }
        if (typeof edge.id !== "string" || !ID.test(edge.id)) {
            issue(issues, "INVALID_EDGE_ID", path + ".id", "edge id must be canonical");
        }
        else {
            if (edgeIds.has(edge.id))
                issue(issues, "DUPLICATE_EDGE_ID", path + ".id", "edge id must be unique");
            edgeIds.add(edge.id);
        }
        if (typeof edge.source !== "string" || !nodeIds.has(edge.source)) {
            issue(issues, "UNKNOWN_EDGE_SOURCE", path + ".source", "edge source must reference a canvas node");
        }
        if (typeof edge.target !== "string" || !nodeIds.has(edge.target)) {
            issue(issues, "UNKNOWN_EDGE_TARGET", path + ".target", "edge target must reference a canvas node");
        }
        if (edge.source === edge.target)
            issue(issues, "SELF_EDGE_FORBIDDEN", path, "self edges are not allowed");
        if (typeof edge.source === "string" && typeof edge.target === "string") {
            const pair = edge.source + "->" + edge.target;
            if (edgePairs.has(pair))
                issue(issues, "DUPLICATE_EDGE", path, "duplicate directed edges are not allowed");
            edgePairs.add(pair);
        }
    });
    const viewport = canvas.viewport;
    if (!viewport ||
        typeof viewport.x !== "number" ||
        !Number.isFinite(viewport.x) ||
        typeof viewport.y !== "number" ||
        !Number.isFinite(viewport.y) ||
        typeof viewport.zoom !== "number" ||
        !Number.isFinite(viewport.zoom) ||
        viewport.zoom < 0.1 ||
        viewport.zoom > 4) {
        issue(issues, "INVALID_VIEWPORT", "viewport", "viewport must contain finite x/y and zoom from 0.1 to 4");
    }
    return Object.freeze({ valid: issues.length === 0, issues: Object.freeze(issues) });
}
export function emptySolutionCanvasDraft(name = "Nueva solución") {
    return Object.freeze({
        schemaVersion: 1,
        name,
        description: "",
        nodes: Object.freeze([]),
        edges: Object.freeze([]),
        viewport: Object.freeze({ x: 0, y: 0, zoom: 1 }),
    });
}
//# sourceMappingURL=canvas.js.map